import { z } from "zod";
export const seedDefaultAgentProfileInputSchema = z.object({
    createdBy: z.string().trim().min(1).max(120).optional(),
});
export const seedDefaultAgentProfileResponseSchema = z.object({
    agentProfileId: z.string(),
    taskRouterVersionId: z.string(),
    skillVersionId: z.string(),
    ambientContextVersionId: z.string(),
    created: z.boolean(),
});
export const backfillLegacyRunModelsInputSchema = z.object({
    limit: z.number().int().positive().optional(),
});
export const backfillLegacyRunModelsResponseSchema = z.object({
    updated: z.number().int().nonnegative(),
    inspectedCompleted: z.number().int().nonnegative(),
    inspectedFailed: z.number().int().nonnegative(),
});
// AGE-7: admin tenant + key management.
export const createAdminTenantInputSchema = z.object({
    tenantSlug: z.string().min(1).max(64),
    displayName: z.string().trim().min(1).max(256),
});
export const createAdminTenantResponseSchema = z.object({
    tenantSlug: z.string(),
    displayName: z.string(),
    createdAt: z.number(),
});
export const mintTenantApiKeyInputSchema = z.object({
    label: z.string().trim().min(1).max(256),
    expiresAt: z.number().int().positive().optional(),
    connectorOrganisationIds: z
        .array(z.string().trim().min(1).max(256))
        .max(500)
        .default([]),
    connectorCapabilities: z
        .array(z.enum(["connectors:read", "connectors:write", "connectors:sync"]))
        .max(3)
        .default([]),
});
export const mintTenantApiKeyResponseSchema = z.object({
    keyId: z.string(),
    token: z.string(),
    label: z.string(),
    connectorOrganisationIds: z.array(z.string()),
    connectorCapabilities: z.array(z.enum(["connectors:read", "connectors:write", "connectors:sync"])),
    expiresAt: z.number(),
    createdAt: z.number(),
});
export const revokeTenantApiKeyResponseSchema = z.object({
    keyId: z.string(),
    revokedAt: z.number(),
});
// AGE-14: tenant warehouse scope CRUD.
export const addTenantWarehouseScopeInputSchema = z.object({
    organisationId: z.string().trim().min(1).max(256),
});
export const tenantWarehouseScopeResponseSchema = z.object({
    tenantSlug: z.string(),
    organisationId: z.string(),
    created: z.boolean(),
});
export const listTenantWarehouseScopesResponseSchema = z.object({
    organisationIds: z.array(z.string()),
});
// Twin Build worker authorities: which worker authors for an organisation, with which
// frozen profile. A worker ID serves one organisation.
export const TWIN_AUTHORING_WORKER_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,119}$/u;
export const TWIN_AUTHORING_PROFILE_ID_PATTERN = /^[a-z0-9][a-z0-9_.:-]{0,127}$/u;
export const putTwinAuthoringAuthorityInputSchema = z.object({
    workerId: z.string().regex(TWIN_AUTHORING_WORKER_ID_PATTERN),
    profileId: z.string().regex(TWIN_AUTHORING_PROFILE_ID_PATTERN),
}).strict();
export const twinAuthoringAuthoritySchema = z.object({
    tenantSlug: z.string(),
    organisationId: z.string(),
    workerId: z.string(),
    profileId: z.string(),
}).strict();
export const listTwinAuthoringAuthoritiesResponseSchema = z.object({
    authorities: z.array(twinAuthoringAuthoritySchema),
}).strict();
// A tenant's allowed capabilities (tenants.allowed_capabilities): the ceiling of what its runs may be granted. An
// operator turns one on or off through the tenant's admin API (`fleet tenant capabilities`), one at a time, each
// change audited with who made it and the list before and after. Only the capabilities listed here may be turned on
// or off that way; reading the Twin is always allowed and is never one of them.
export const tenantOperatorCapabilitySchema = z.enum(["web-research"]);
/** Who turns a capability on or off: the operator's own name, as the audit keeps it. */
export const changeTenantCapabilityInputSchema = z.object({
    operator: z.string().trim().min(1).max(200),
}).strict();
export const tenantCapabilitiesResponseSchema = z.object({
    tenantSlug: z.string(),
    /** The tenant's stored list (tenants.allowed_capabilities): what its runs may be granted; reading the Twin always. */
    allowed: z.array(z.string()),
}).strict();
export const tenantCapabilityChangeResponseSchema = tenantCapabilitiesResponseSchema.extend({
    capability: tenantOperatorCapabilitySchema,
    /** "unchanged" when the tenant already allowed it (turned on) or didn't (turned off): nothing is written or audited. */
    change: z.enum(["added", "removed", "unchanged"]),
    before: z.array(z.string()),
}).strict();
